import { queryOptions, useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import type { TaskScope, TaskType } from "@recobra/schemas/task-catalog";

import { taskCatalogApi } from "../../lib/api";

export const taskCatalogQueryKey = ["task-catalog"] as const;

export type TaskTypes = ReadonlyMap<string, TaskType>;

/** Every task type of every scope, which names and configures tasks wherever they appear. */
export const taskTypesQueryOptions = queryOptions({
  queryKey: [...taskCatalogQueryKey, "types"],
  queryFn: () => taskCatalogApi.list(),
  staleTime: Infinity,
});

const byId = ({ taskTypes }: { taskTypes: TaskType[] }): TaskTypes =>
  new Map(taskTypes.map((type) => [type.id, type]));

/** Every task type, by id. */
export const useTaskTypes = () => useQuery({ ...taskTypesQueryOptions, select: byId });

/** One scope's task types, by id; idle until the scope is known. */
export const useScopeTaskTypes = (scope: TaskScope | undefined) =>
  useQuery({
    ...taskTypesQueryOptions,
    enabled: scope !== undefined,
    select: useCallback(
      ({ taskTypes }: { taskTypes: TaskType[] }): TaskTypes =>
        new Map(taskTypes.filter((type) => type.scope === scope).map((type) => [type.id, type])),
      [scope],
    ),
  });

/** Names a task type; an id no task type has reads as itself. */
export type TaskName = (taskType: string) => string;

export const useTaskName = (): TaskName => {
  const { data } = useTaskTypes();
  return useCallback((taskType) => data?.get(taskType)?.name ?? taskType, [data]);
};

/** The active task types, which new work can still reach. */
export const activeTaskTypes = (taskTypes: TaskTypes | undefined): TaskType[] =>
  [...(taskTypes?.values() ?? [])].filter(({ active }) => active);
